import { useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { getAllAccolades } from '../../actions/accoladeActions'
import { stockAccolades } from '../../accolades/accolades'
import { blurOnEnter } from '../../utility/certificationBuilder'
import { lessonOrderOptions, findOption } from './certificationOptions'
import { labelledSelectComponents } from './selectComponents'
import SettingsCard from './SettingsCard'
import BuilderField from './BuilderField'
import Input from '../common/form/Input'
import Select from '../common/form/Select'

import classes from './CompletionCard.module.scss'

const CERTIFIED_ACCOLADE_PATTERN = /certified/i

/* eslint-disable react/prop-types */
const AccoladeLabel = ({ data }) => (
  <span className={classNames('d-inline-flex align-items-center', classes.accolade)}>
    {data.badge && <img src={data.badge} alt='' className={classes.accoladeBadge} />}
    {data.label}
  </span>
)

/* eslint-enable react/prop-types */

const ACCOLADE_COMPONENTS = labelledSelectComponents(AccoladeLabel)

const CompletionCard = ({ form, errors, onChange, onBlur, onSelect }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const accolades = useSelector(state => state.accolades.all_accolades || [])
  const lessonOrders = lessonOrderOptions()

  useEffect(() => {
    if (accolades.length === 0) { dispatch(getAllAccolades()) }
  }, [dispatch, accolades.length])

  const accoladeOptions = [
    { value: null, label: t('certificationBuilder.details.accoladeNone') },
    ...accolades
      .filter(accolade => CERTIFIED_ACCOLADE_PATTERN.test(accolade.name))
      .map(accolade => ({ value: accolade.id, label: accolade.name, badge: stockAccolades[accolade.name]?.badge })),
  ]

  return (
    <SettingsCard eyebrow={t('certificationBuilder.details.completion')}>
      <div className={classes.row}>
        <BuilderField label={t('certificationBuilder.details.lessonOrder')} htmlFor='certificationLessonOrder'>
          <Select
            inputId='certificationLessonOrder'
            options={lessonOrders}
            value={findOption(lessonOrders, form.sequential)}
            onChange={(option) => onSelect('sequential', option.value)}
            isSearchable={false}
          />
        </BuilderField>
        <BuilderField label={t('certificationBuilder.details.finalPassMark')} htmlFor='certificationPassMark'>
          <Input
            id='certificationPassMark'
            type='number'
            min={1}
            max={100}
            value={form.passMark}
            endAdornment={<span className={classes.adornment}>%</span>}
            onChange={(e) => onChange('passMark', e.target.value)}
            onBlur={() => onBlur('passMark')}
            onKeyDown={blurOnEnter}
            error={errors.passMark}
          />
        </BuilderField>
      </div>

      <BuilderField label={t('certificationBuilder.details.accolade')} htmlFor='certificationAccolade'>
        <Select
          inputId='certificationAccolade'
          options={accoladeOptions}
          value={accoladeOptions.find(option => option.value === form.accoladeId) || accoladeOptions[0]}
          components={ACCOLADE_COMPONENTS}
          onChange={(option) => onSelect('accoladeId', option.value)}
          isSearchable={false}
        />
      </BuilderField>
    </SettingsCard>
  )
}

CompletionCard.propTypes = {
  form: PropTypes.shape({
    sequential: PropTypes.bool.isRequired,
    accoladeId: PropTypes.number,
    passMark: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default CompletionCard
